/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import { CodedError } from '@karya/error-values';
import { StoreNames, IndexNames, IndexKey, StoreKey } from 'idb';
import { KaryaDB } from '../Setup';

/**
 * Details about an IndexedDB error.
 */
type IndexedDBErrorDetails = {
  operation: 'get' | 'add' | 'put' | 'delete' | 'count' | 'open' | 'transaction' | 'clear' | 'other';
  cause?: unknown;
  storeName?: StoreNames<KaryaDB>;
  key?: unknown;
  indexName?: string;
  value?: unknown;
};

/**
 * Error indicating that an IndexedDB operation failed.
 */
export class IndexedDBError extends CodedError<'IndexedDBError', IndexedDBErrorDetails> {
  constructor(details: IndexedDBErrorDetails) {
    super('IndexedDBError', 'An error occurred while performing an IndexedDB operation.', details);
  }
}

/**
 * Details about a record not found error.
 */
export type RecordNotFoundErrorDetails<
  Store extends StoreNames<KaryaDB>,
  IndexName extends IndexNames<KaryaDB, Store> | never,
> = {
  storeName: string;
  key: StoreKey<KaryaDB, Store> | IndexKey<KaryaDB, Store, IndexName>;
  indexName?: IndexName;
};

/**
 * Error indicating that a record was not found in the database.
 */
export class RecordNotFoundError<
  Store extends StoreNames<KaryaDB>,
  IndexName extends IndexNames<KaryaDB, Store> | never,
> extends CodedError<'RecordNotFoundError', RecordNotFoundErrorDetails<Store, IndexName>> {
  constructor(details: RecordNotFoundErrorDetails<Store, IndexName>) {
    let message = `Record not found in store "${details.storeName}" for key "${details.key}".`;
    if (details.indexName) {
      message += ` (In Index "${details.indexName}").`;
    }
    super('RecordNotFoundError', message, details);
  }
}

/**
 * Error type with implicit possibility of IndexedDBError
 * Helps to reduce boilerplate by not having to explicitly add IndexedDBError in every error union type.
 */
export type DbError<Kind> = Kind | IndexedDBError;
